import * as QRCode from "qrcode";
import { useId, useMemo } from "react";

export interface QrCodeSvgProps {
  /** The exact payload the code encodes (a URL, in Owner Admin's case). */
  value: string;
  /** Rendered pixel size (square). @default 144 */
  size?: number;
  className?: string;
  /** Accessible label; when omitted the SVG is marked decorative. */
  ariaLabel?: string;
}

/**
 * A real, scannable QR code rendered as inline SVG — no canvas, no network
 * fetch, no external QR-image service. Uses `qrcode`'s synchronous `create()`
 * to get the module matrix, then draws one `<rect>` per dark module.
 */
export function QrCodeSvg({ value, size = 144, className, ariaLabel }: QrCodeSvgProps) {
  const titleId = useId();
  const qr = useMemo(() => QRCode.create(value, { errorCorrectionLevel: "M" }), [value]);
  const moduleCount = qr.modules.size;
  const margin = 2; // quiet zone, in modules, so scanners have clear border
  const viewBoxSize = moduleCount + margin * 2;

  const cells: { x: number; y: number }[] = [];
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (qr.modules.get(row, col)) cells.push({ x: col + margin, y: row + margin });
    }
  }

  return (
    <svg
      role="img"
      aria-labelledby={titleId}
      aria-hidden={ariaLabel ? undefined : true}
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      width={size}
      height={size}
      className={className}
      shapeRendering="crispEdges"
    >
      <title id={titleId}>{ariaLabel ?? "QR code"}</title>
      <rect x={0} y={0} width={viewBoxSize} height={viewBoxSize} fill="#ffffff" />
      {cells.map((cell) => (
        <rect
          key={`${cell.x}-${cell.y}`}
          x={cell.x}
          y={cell.y}
          width={1}
          height={1}
          fill="#0a0a0a"
        />
      ))}
    </svg>
  );
}

/**
 * Renders the same payload to a downloadable PNG data URL (browser canvas
 * renderer under the hood). Used for the "Download PNG" action — kept async
 * and separate from `QrCodeSvg` so the on-screen preview never depends on
 * canvas availability.
 */
export async function qrCodeToPngDataUrl(value: string, pixelWidth = 512): Promise<string> {
  return QRCode.toDataURL(value, {
    errorCorrectionLevel: "M",
    margin: 2,
    width: pixelWidth,
  });
}
